import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FilePenLine,
  Info,
  RefreshCw,
  UserRound,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { blogCreationService } from '@/services/blogCreationService';
import type {
  HumanQaDetailResponse,
  HumanQaQueueResponse,
} from '@/services/blogCreationService';
import type { HumanQaStatus } from '@/lib/humanQa';
import {
  buildMetadataFieldEvidence,
  type MetadataEvidenceCategory,
  type MetadataFieldEvidence,
} from '@/lib/metadataEvidence';
import { getHumanVerifyState, type HumanVerifyItem, type HumanVerifyItemStatus } from '@/lib/humanVerify';
import { buildGateItem } from '@/lib/prePublishGate';
import type { MetadataTier, MetadataViolation } from '@/lib/metadataCriteria';
import { cn } from '@/lib/utils';
import { universalBlogEditorPath } from '@/utils/editorRoutes';
import { HumanVerifyChecklistSection } from '@/components/human-verify/HumanVerifyChecklistSection';

type StatusFilter = HumanQaStatus;

interface HumanQaReviewWorkbenchProps {
  initialTenantId?: string | null;
  defaultReviewer?: string;
}

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const FIELD_ROWS: Array<{ field: string; label: string; detailKey: keyof HumanQaDetailResponse }> = [
  { field: 'title', label: 'H1 / Title', detailKey: 'title' },
  { field: 'meta_title', label: 'Meta Title', detailKey: 'meta_title' },
  { field: 'meta_description', label: 'Meta Description', detailKey: 'meta_description' },
  { field: 'url_slug', label: 'Slug', detailKey: 'url_slug' },
];

function normalizeReason(reason: string): string {
  return reason
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatAge(value?: string | null): string {
  if (!value) return 'Unknown';
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return 'Unknown';
  const diffMs = Date.now() - ts;
  if (diffMs < 60_000) return 'Now';
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h`;
  return `${Math.floor(diffMs / 86_400_000)}d`;
}

function formatDateTime(value?: string | null): string {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function tierClass(tier: MetadataTier): string {
  if (tier === 'block') return 'border-destructive/30 bg-destructive/5 text-destructive';
  if (tier === 'warn') return 'border-coral/30 bg-coral-light text-coral-dark';
  return 'border-teal/30 bg-teal-light text-teal-dark';
}

function tierIcon(tier: MetadataTier) {
  if (tier === 'block') return <AlertCircle className="h-3.5 w-3.5" />;
  if (tier === 'warn') return <AlertTriangle className="h-3.5 w-3.5" />;
  return <Info className="h-3.5 w-3.5" />;
}

function TierChip({ tier, children }: { tier: MetadataTier; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
        tierClass(tier),
      )}
    >
      {tierIcon(tier)}
      {children}
    </span>
  );
}

function StatusChip({ status }: { status: HumanQaStatus }) {
  const classes =
    status === 'approved'
      ? 'border-teal/30 bg-teal-light text-teal-dark'
      : status === 'rejected'
        ? 'border-destructive/30 bg-destructive/5 text-destructive'
        : 'border-coral/30 bg-coral-light text-coral-dark';
  return (
    <span className={cn('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold', classes)}>
      {status}
    </span>
  );
}

function ReasonChips({ reasons }: { reasons: string[] }) {
  if (!reasons.length) {
    return <span className="text-xs text-muted-foreground">review required</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((reason) => (
        <span
          key={reason}
          className="rounded-md border border-coral/30 bg-coral-light px-2 py-0.5 text-xs font-medium text-coral-dark"
        >
          {normalizeReason(reason)}
        </span>
      ))}
    </div>
  );
}

function buildWpEditUrl(tenantId?: string | null, postId?: number | null): string | null {
  if (!tenantId || !postId) return null;
  const host = tenantId.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (!host) return null;
  return `https://${host}/wp-admin/post.php?post=${postId}&action=edit`;
}

function EvidencePill({ label, active, fallback }: { label: string; active: boolean; fallback?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium',
        fallback
          ? 'border-coral/30 bg-coral-light text-coral-dark'
          : active
            ? 'border-teal/30 bg-teal-light text-teal-dark'
            : 'border-border bg-muted text-muted-foreground',
      )}
    >
      {fallback ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
      {label}: {fallback ? 'Fallback' : active ? 'Applied' : 'Not applied'}
    </span>
  );
}

function fieldEvidenceClass(category: MetadataEvidenceCategory): string {
  if (category === 'fallback_review') return 'border-coral/30 bg-coral-light text-coral-dark';
  if (category === 'ai' || category === 'your_edit_kept' || category === 'frame_cap_kept_current_title') {
    return 'border-teal/30 bg-teal-light text-teal-dark';
  }
  return 'border-border bg-muted text-muted-foreground';
}

function fieldEvidenceIcon(category: MetadataEvidenceCategory) {
  if (category === 'your_edit_kept') return <FilePenLine className="h-3.5 w-3.5" />;
  if (category === 'frame_cap_kept_current_title') return <ClipboardCheck className="h-3.5 w-3.5" />;
  if (category === 'fallback_review') return <AlertTriangle className="h-3.5 w-3.5" />;
  if (category === 'ai') return <CheckCircle2 className="h-3.5 w-3.5" />;
  if (category === 'not_generated') return <XCircle className="h-3.5 w-3.5" />;
  return <Info className="h-3.5 w-3.5" />;
}

function FieldEvidencePill({ evidence }: { evidence: MetadataFieldEvidence }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium',
        fieldEvidenceClass(evidence.category),
      )}
    >
      {fieldEvidenceIcon(evidence.category)}
      {evidence.label}
    </span>
  );
}

function MetadataReviewFields({
  detail,
  violations,
}: {
  detail: HumanQaDetailResponse;
  violations: MetadataViolation[];
}) {
  return (
    <section aria-labelledby="metadata-review-heading" className="space-y-3">
      <div>
        <h2 id="metadata-review-heading" className="text-sm font-semibold text-foreground">
          Metadata Review
        </h2>
        <p className="text-xs text-muted-foreground">
          Same evaluator and rule tiers used by the pre-publish gate.
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {FIELD_ROWS.map((row) => {
          const fieldViolations = violations.filter((violation) => violation.field === row.field);
          const value = detail[row.detailKey];
          return (
            <div key={row.field} className="rounded-md border border-border bg-warm-bg p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {row.label}
                </span>
                {fieldViolations.length === 0 ? (
                  <span className="rounded-md border border-teal/30 bg-teal-light px-2 py-0.5 text-xs font-medium text-teal-dark">
                    Clean
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {fieldViolations.length} issue{fieldViolations.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <p className="min-h-10 break-words text-sm text-foreground">
                {typeof value === 'string' && value.trim() ? value : 'Missing'}
              </p>
              {fieldViolations.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {fieldViolations.map((violation) => (
                    <TierChip key={violation.ruleId} tier={violation.tier}>
                      {violation.ruleId.replace(/_/g, ' ')}
                    </TierChip>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function WarningList({ violations }: { violations: MetadataViolation[] }) {
  const ordered = [...violations].sort((a, b) => {
    const weight: Record<MetadataTier, number> = { block: 0, warn: 1, info: 2 };
    return weight[a.tier] - weight[b.tier];
  });

  return (
    <section aria-labelledby="warnings-heading" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id="warnings-heading" className="text-sm font-semibold text-foreground">
          Warnings
        </h2>
        <span className="text-xs text-muted-foreground">{ordered.length} total</span>
      </div>
      {ordered.length === 0 ? (
        <div className="rounded-md border border-teal/30 bg-teal-light px-3 py-2 text-sm text-teal-dark">
          No evaluator warnings for the selected metadata.
        </div>
      ) : (
        <ul className="space-y-2">
          {ordered.map((violation) => (
            <li
              key={`${violation.field}-${violation.ruleId}`}
              className={cn('rounded-md border px-3 py-2 text-sm', tierClass(violation.tier))}
            >
              <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
                {tierIcon(violation.tier)}
                {violation.tier} / {violation.field.replace(/_/g, ' ')}
              </div>
              <div>{violation.message}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function GenerationEvidence({ detail }: { detail: HumanQaDetailResponse }) {
  const metadata = detail.postgen?.metadata;
  const social = detail.postgen?.social;
  const fieldEvidence = buildMetadataFieldEvidence(detail.postgen);

  return (
    <section aria-labelledby="evidence-heading" className="space-y-3">
      <h2 id="evidence-heading" className="text-sm font-semibold text-foreground">
        Generation Evidence
      </h2>
      <div className="flex flex-wrap gap-2">
        <EvidencePill label="Metadata" active={Boolean(metadata?.applied)} fallback={Boolean(metadata?.fallback_fired)} />
        <EvidencePill label="Social" active={Boolean(social?.applied)} fallback={Boolean(social?.fallback_fired)} />
      </div>
      {fieldEvidence.length > 0 && (
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {fieldEvidence.map((evidence) => (
            <div key={evidence.field} className="rounded-md border border-border bg-muted px-3 py-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {evidence.field.replace(/_/g, ' ')}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                <FieldEvidencePill evidence={evidence} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AccountabilityTimeline({ detail }: { detail: HumanQaDetailResponse }) {
  const confirmations = [...(detail.publish_confirmations ?? [])].sort(
    (a, b) => Date.parse(b.confirmed_at) - Date.parse(a.confirmed_at),
  );

  return (
    <section aria-labelledby="timeline-heading" className="space-y-3">
      <h2 id="timeline-heading" className="text-sm font-semibold text-foreground">
        Accountability Timeline
      </h2>
      {confirmations.length === 0 ? (
        <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
          No publish overrides have been recorded yet.
        </div>
      ) : (
        <ol className="space-y-3">
          {confirmations.map((confirmation, index) => (
            <li key={`${confirmation.confirmed_at}-${index}`} className="border-l-2 border-coral pl-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-foreground">{confirmation.confirmed_by || 'Unknown user'}</span>
                <span className="text-muted-foreground">{formatDateTime(confirmation.confirmed_at)}</span>
              </div>
              {confirmation.reviewer && (
                <div className="mt-1 text-xs text-muted-foreground">Reviewer: {confirmation.reviewer}</div>
              )}
              {confirmation.justification && (
                <div className="mt-2 rounded-md bg-warm-bg px-3 py-2 text-sm text-foreground">
                  {confirmation.justification}
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {confirmation.rules.map((rule) => (
                  <TierChip key={`${rule.id}-${rule.message}`} tier={rule.tier}>
                    {rule.message}
                  </TierChip>
                ))}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function HumanQaReviewWorkbench({
  initialTenantId,
  defaultReviewer = '',
}: HumanQaReviewWorkbenchProps) {
  const queryClient = useQueryClient();
  const [tenantFilter, setTenantFilter] = React.useState(initialTenantId ?? '');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('pending');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [hiddenIds, setHiddenIds] = React.useState<Set<string>>(() => new Set());
  const [assignmentInput, setAssignmentInput] = React.useState('');
  const [reviewerInput, setReviewerInput] = React.useState(defaultReviewer);
  const [decisionNotes, setDecisionNotes] = React.useState('');

  React.useEffect(() => {
    if (initialTenantId && !tenantFilter) setTenantFilter(initialTenantId);
  }, [initialTenantId, tenantFilter]);

  React.useEffect(() => {
    setHiddenIds(new Set());
  }, [tenantFilter, statusFilter]);

  React.useEffect(() => {
    setReviewerInput(defaultReviewer);
  }, [defaultReviewer]);

  const normalizedTenant = tenantFilter.trim();
  const queueQueryKey = React.useMemo(
    () => ['human-qa', 'queue', normalizedTenant, statusFilter] as const,
    [normalizedTenant, statusFilter],
  );
  const detailQueryKey = React.useMemo(
    () => ['human-qa', 'detail', normalizedTenant, selectedId] as const,
    [normalizedTenant, selectedId],
  );

  const queueQuery = useQuery({
    queryKey: queueQueryKey,
    queryFn: () => blogCreationService.listHumanQaQueue({ tenantId: normalizedTenant, status: statusFilter }),
    enabled: normalizedTenant.length > 0,
    staleTime: 30_000,
  });

  const rows = React.useMemo(
    () => (queueQuery.data?.items ?? []).filter((row) => !hiddenIds.has(row.id)),
    [hiddenIds, queueQuery.data?.items],
  );
  const selectedItem = React.useMemo(
    () => rows.find((row) => row.id === selectedId) ?? queueQuery.data?.items.find((row) => row.id === selectedId) ?? null,
    [queueQuery.data?.items, rows, selectedId],
  );

  const detailQuery = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => blogCreationService.getHumanQaDetail(selectedId!, normalizedTenant),
    enabled: normalizedTenant.length > 0 && Boolean(selectedId),
    staleTime: 15_000,
  });

  const detail = detailQuery.data ?? null;
  const gateItem = React.useMemo(() => {
    if (!detail || !selectedItem) return null;
    return buildGateItem(
      {
        id: selectedItem.id,
        title: detail.title,
        meta_title: detail.meta_title,
        meta_description: detail.meta_description,
        url_slug: detail.url_slug,
        keyword: detail.keyword,
        status: detail.status,
        human_verify: detail.human_verify ?? undefined,
        generation_metadata: {
          human_qa: detail.human_qa ?? undefined,
          human_verify: detail.human_verify ?? undefined,
        },
      },
      detail.tenant_id ?? normalizedTenant,
    );
  }, [detail, normalizedTenant, selectedItem]);
  const violations = (gateItem?.violations ?? []).filter((violation) => violation.field !== 'human_verify');
  const humanVerify = React.useMemo(
    () => getHumanVerifyState({ human_verify: detail?.human_verify }),
    [detail?.human_verify],
  );

  React.useEffect(() => {
    setAssignmentInput(detail?.human_qa?.assigned_to ?? selectedItem?.assigned_to ?? '');
  }, [detail?.human_qa?.assigned_to, selectedItem?.assigned_to]);

  React.useEffect(() => {
    setDecisionNotes('');
  }, [selectedId]);

  const assignMutation = useMutation({
    mutationFn: (assignedTo: string) =>
      blogCreationService.reviewHumanQa(selectedId!, { action: 'assign', assigned_to: assignedTo }, normalizedTenant),
    onSuccess: (response) => {
      queryClient.setQueryData<HumanQaDetailResponse>(detailQueryKey, (current) =>
        current ? { ...current, human_qa: response.human_qa } : current,
      );
      queryClient.setQueryData<HumanQaQueueResponse>(queueQueryKey, (current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.id === response.content_id ? { ...item, assigned_to: response.human_qa.assigned_to } : item,
              ),
            }
          : current,
      );
      toast.success('Assignment saved');
    },
    onError: () => {
      toast.error('Failed to save assignment');
    },
  });

  const decisionMutation = useMutation({
    mutationFn: (body: { action: 'approve' | 'reject'; reviewer: string; notes?: string }) =>
      blogCreationService.reviewHumanQa(selectedId!, body, normalizedTenant),
    onMutate: async (body) => {
      if (statusFilter === 'pending' && selectedId) {
        setHiddenIds((current) => new Set(current).add(selectedId));
      }
      return { contentId: selectedId, action: body.action };
    },
    onSuccess: (response) => {
      queryClient.setQueryData<HumanQaDetailResponse>(detailQueryKey, (current) =>
        current ? { ...current, human_qa: response.human_qa } : current,
      );
      queryClient.invalidateQueries({ queryKey: ['human-qa', 'queue'] });
      toast.success(response.human_qa.status === 'approved' ? 'Human QA approved' : 'Human QA rejected');
    },
    onError: (_error, _body, context) => {
      if (context?.contentId) {
        setHiddenIds((current) => {
          const next = new Set(current);
          next.delete(context.contentId);
          return next;
        });
      }
      toast.error('Failed to submit QA decision');
    },
  });

  const humanVerifyMutation = useMutation({
    mutationFn: (body: {
      item: HumanVerifyItem;
      status: Extract<HumanVerifyItemStatus, 'verified' | 'dismissed'>;
      note?: string;
      reviewer: string;
    }) =>
      blogCreationService.resolveHumanVerifyItem(
        selectedId!,
        {
          item_id: body.item.id,
          status: body.status,
          reviewer: body.reviewer,
          ...(body.note ? { note: body.note } : {}),
        },
        normalizedTenant,
      ),
    onSuccess: (response) => {
      queryClient.setQueryData<HumanQaDetailResponse>(detailQueryKey, (current) =>
        current ? { ...current, human_verify: response.human_verify } : current,
      );
      toast.success('Reviewer sign-off saved');
    },
    onError: () => {
      toast.error('Failed to save reviewer sign-off');
    },
  });

  const canApproveOrReject = reviewerInput.trim().length > 0 && !decisionMutation.isPending;
  const canReject = canApproveOrReject && decisionNotes.trim().length > 0;
  const wpEditUrl = buildWpEditUrl(detail?.tenant_id ?? normalizedTenant, detail?.wordpress_post_id);
  const pendingHumanVerifyItemId = humanVerifyMutation.variables?.item.id ?? null;

  const submitDecision = (action: 'approve' | 'reject') => {
    const reviewer = reviewerInput.trim();
    const notes = decisionNotes.trim();
    if (!reviewer) return;
    if (action === 'reject' && !notes) return;
    decisionMutation.mutate({
      action,
      reviewer,
      ...(notes ? { notes } : {}),
    });
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 border-b border-border pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-coral-dark">
            <ClipboardCheck className="h-4 w-4" />
            Human QA Accountability
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">QA Review</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Review content routed by generation metadata before the team publishes or overrides warnings.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Tenant
            <input
              aria-label="Workspace filter"
              value={tenantFilter}
              onChange={(event) => setTenantFilter(event.target.value)}
              className="h-9 min-w-64 rounded-md border border-border bg-background px-3 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coral"
              placeholder="workspace domain"
            />
          </label>
          <button
            type="button"
            onClick={() => queueQuery.refetch()}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground hover:bg-muted"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border border-border bg-muted p-1" aria-label="QA status filter">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              aria-pressed={statusFilter === filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={cn(
                'rounded px-3 py-1.5 text-sm font-medium transition',
                statusFilter === filter.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="text-sm text-muted-foreground">
          {queueQuery.isLoading ? 'Loading queue...' : `${rows.length} ${statusFilter} item${rows.length === 1 ? '' : 's'}`}
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(460px,1.05fr)]">
        <section className="min-w-0 overflow-hidden rounded-md border border-border bg-card" aria-labelledby="queue-heading">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 id="queue-heading" className="text-sm font-semibold text-foreground">
              Queue
            </h2>
            <StatusChip status={statusFilter} />
          </div>

          {!normalizedTenant && (
            <div className="p-6 text-sm text-muted-foreground">Enter a workspace domain to load QA items.</div>
          )}
          {normalizedTenant && queueQuery.isError && (
            <div className="p-6 text-sm text-destructive">Failed to load human QA queue.</div>
          )}
          {normalizedTenant && queueQuery.isLoading && (
            <div className="p-6 text-sm text-muted-foreground">Loading human QA queue...</div>
          )}
          {normalizedTenant && !queueQuery.isLoading && rows.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground">No {statusFilter} human QA items for this workspace.</div>
          )}

          {rows.length > 0 && (
            <div className="overflow-auto">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead className="border-b border-border bg-muted text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Workspace</th>
                    <th className="px-3 py-2 font-semibold">Title</th>
                    <th className="px-3 py-2 font-semibold">Keyword</th>
                    <th className="px-3 py-2 font-semibold">Reasons</th>
                    <th className="px-3 py-2 font-semibold">Age</th>
                    <th className="px-3 py-2 font-semibold">Assigned To</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn(
                        'cursor-pointer bg-card transition hover:bg-warm-bg',
                        selectedId === row.id && 'bg-warm-bg',
                      )}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <td className="px-3 py-3 align-top text-xs text-muted-foreground">{row.tenant_id}</td>
                      <td className="max-w-72 px-3 py-3 align-top font-medium text-foreground">
                        <button type="button" className="line-clamp-2 text-left" onClick={() => setSelectedId(row.id)}>
                          {row.title || 'Untitled article'}
                        </button>
                      </td>
                      <td className="max-w-52 px-3 py-3 align-top text-muted-foreground">
                        <span className="line-clamp-2">{row.keyword || 'No keyword'}</span>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <ReasonChips reasons={row.reasons ?? []} />
                      </td>
                      <td className="px-3 py-3 align-top text-muted-foreground">{formatAge(row.created_at)}</td>
                      <td className="px-3 py-3 align-top text-muted-foreground">
                        {row.assigned_to || 'Unassigned'}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <StatusChip status={row.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="min-w-0 rounded-md border border-border bg-card" aria-label="Human QA review panel">
          {!selectedId && (
            <div className="flex min-h-[420px] flex-col items-center justify-center gap-2 p-8 text-center">
              <ClipboardCheck className="h-8 w-8 text-coral" />
              <h2 className="text-base font-semibold text-foreground">Select a QA item</h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                Pick a row to inspect metadata evidence, assign ownership, and record a decision.
              </p>
            </div>
          )}

          {selectedId && detailQuery.isLoading && (
            <div className="p-6 text-sm text-muted-foreground">Loading review detail...</div>
          )}

          {selectedId && detailQuery.isError && (
            <div className="p-6 text-sm text-destructive">Failed to load review detail.</div>
          )}

          {detail && selectedItem && (
            <div className="flex max-h-[calc(100vh-15rem)] flex-col">
              <div className="border-b border-border px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="line-clamp-2 text-base font-semibold text-foreground">
                      {detail.title || selectedItem.title || 'Untitled article'}
                    </h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{detail.keyword || selectedItem.keyword || 'No keyword'}</span>
                      {detail.human_qa?.status && <StatusChip status={detail.human_qa.status} />}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {wpEditUrl && (
                      <a
                        href={wpEditUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        WP draft
                      </a>
                    )}
                    <Link
                      to={universalBlogEditorPath(selectedItem.id)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <FilePenLine className="h-3.5 w-3.5" />
                      Core editor
                    </Link>
                  </div>
                </div>
              </div>

              <div className="flex-1 space-y-6 overflow-auto p-4">
                <MetadataReviewFields detail={detail} violations={violations} />
                <WarningList violations={violations} />
                <GenerationEvidence detail={detail} />
                <HumanVerifyChecklistSection
                  humanVerify={humanVerify}
                  pendingItemId={humanVerifyMutation.isPending ? pendingHumanVerifyItemId : null}
                  reviewerAvailable={reviewerInput.trim().length > 0}
                  onResolve={(item, status, note) => {
                    const reviewer = reviewerInput.trim();
                    if (!reviewer) return;
                    humanVerifyMutation.mutate({ item, status, note, reviewer });
                  }}
                />

                <section aria-labelledby="human-qa-heading" className="space-y-3">
                  <div>
                    <h2 id="human-qa-heading" className="text-sm font-semibold text-foreground">
                      Human QA Ownership
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Reasons and assignee are stored in generation metadata.
                    </p>
                  </div>
                  <ReasonChips reasons={detail.human_qa?.reasons ?? selectedItem.reasons ?? []} />
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <label className="flex flex-1 flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Assigned To
                      <input
                        aria-label="Assigned to"
                        value={assignmentInput}
                        onChange={(event) => setAssignmentInput(event.target.value)}
                        className="h-9 rounded-md border border-border bg-background px-3 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coral"
                        placeholder="name or email"
                      />
                    </label>
                    <button
                      type="button"
                      disabled={!assignmentInput.trim() || assignMutation.isPending}
                      onClick={() => assignMutation.mutate(assignmentInput.trim())}
                      className="inline-flex h-9 items-center justify-center gap-2 self-end rounded-md bg-coral px-3 text-sm font-medium text-primary-foreground hover:bg-coral-hover disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                    >
                      <UserRound className="h-4 w-4" />
                      Save Assignee
                    </button>
                  </div>
                </section>

                <AccountabilityTimeline detail={detail} />
              </div>

              <div className="border-t border-border bg-warm-bg p-4">
                <div className="grid gap-3 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
                  <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Reviewer
                    <input
                      aria-label="Reviewer"
                      value={reviewerInput}
                      onChange={(event) => setReviewerInput(event.target.value)}
                      className="h-9 rounded-md border border-border bg-background px-3 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coral"
                      placeholder="reviewer name or email"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Notes
                    <textarea
                      aria-label="Decision notes"
                      value={decisionNotes}
                      onChange={(event) => setDecisionNotes(event.target.value)}
                      className="min-h-20 rounded-md border border-border bg-background px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-coral"
                      placeholder="Reject requires notes; approve notes are optional."
                    />
                  </label>
                </div>
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    disabled={!canApproveOrReject}
                    onClick={() => submitDecision('approve')}
                    className="inline-flex items-center gap-2 rounded-md bg-teal px-4 py-2 text-sm font-semibold text-secondary-foreground hover:bg-teal-hover disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={!canReject}
                    onClick={() => submitDecision('reject')}
                    className="inline-flex items-center gap-2 rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default HumanQaReviewWorkbench;
